/**
 * EventSearchPage - Search and browse events by location
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { LocationSearch, EventList } from '../../components/events';
import { searchEvents, getShows } from '../../api/events';
import { getFriendlyErrorMessage } from '../../api/client-error';
import { useAuth } from '../../context/AuthContext';
import { useCheckout } from '../../context/CheckoutContext';
import { buildLocationParams } from '../../utils/location';
import type { TourStop, Show } from '../../api/events';

interface LocationState {
  zip?: string;
  lat?: number;
  lng?: number;
  label?: string;
}

// Radius used for the empty-state recovery search (SDA-257): when the
// primary search at the user's chosen radius returns 0 results, we try once
// more at this much wider radius so a "host a class closer to the theatre"
// path is always reachable.
const FALLBACK_RADIUS_MILES = 150;

export function EventSearchPage() {
  const { isAuthenticated, logout, isLoading: authLoading } = useAuth();
  const { getCartSummary } = useCheckout();
  const cartSummary = getCartSummary();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize location from URL params
  const initialLocation: LocationState = {
    zip: searchParams.get('zip') || undefined,
    lat: searchParams.get('lat') ? Number(searchParams.get('lat')) : undefined,
    lng: searchParams.get('lng') ? Number(searchParams.get('lng')) : undefined,
    label: searchParams.get('q') || searchParams.get('zip') || undefined,
  };

  const [location, setLocation] = useState<LocationState>(initialLocation);
  const [events, setEvents] = useState<TourStop[]>([]);
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Nearest stops from the radius-150 recovery search, shown in the empty state.
  const [fallbackStops, setFallbackStops] = useState<TourStop[]>([]);
  // Generation counter: each effect run claims the next value, and every
  // state update after an await checks it still owns the latest run before
  // writing — stale responses from a superseded search are dropped.
  const searchGenRef = useRef(0);

  const handleLogout = async () => {
    await logout();
  };

  // Filters
  const [selectedShow, setSelectedShow] = useState<string>('');
  const [radius, setRadius] = useState(50);

  // Determine if we have a valid search location
  const hasLocation = !!(location.zip || (location.lat != null && location.lng != null));

  // Location query string for links (preserves lat/lng/q so the detail page
  // doesn't fall back to zip-centroid geocoding — see SDA distance mismatch fix)
  const locationQuery = buildLocationParams({
    zip: location.zip,
    lat: location.lat != null ? String(location.lat) : undefined,
    lng: location.lng != null ? String(location.lng) : undefined,
    q: location.label,
  });

  // Load shows for filter dropdown
  useEffect(() => {
    getShows()
      .then(setShows)
      .catch(console.error);
  }, []);

  // Search events when location or filters change
  useEffect(() => {
    // Claim this run. Any state write below first checks it still owns the
    // latest generation — a superseded search (user changed a filter mid-flight)
    // is dropped instead of clobbering newer results.
    const generation = ++searchGenRef.current;
    setFallbackStops([]);

    if (!hasLocation) {
      setEvents([]);
      return;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const results = await searchEvents({
          zip: location.zip,
          lat: location.lat,
          lng: location.lng,
          radius,
          show_id: selectedShow || undefined,
        });
        if (generation !== searchGenRef.current) return;

        setEvents(results.data);
        setError(null);

        // Empty-state recovery (SDA-257): the extra backend call fires only
        // on empty primary searches, and only below the max fallback radius.
        // Dropping show_id maximizes what the wider search can recover.
        if (results.data.length === 0 && radius < FALLBACK_RADIUS_MILES) {
          try {
            const fallback = await searchEvents({
              zip: location.zip,
              lat: location.lat,
              lng: location.lng,
              radius: FALLBACK_RADIUS_MILES,
              per_page: 50,
            });
            if (generation !== searchGenRef.current) return;

            // The backend caps per_page at 50, so a radius-150 result set
            // with more than 50 stops could still hide the true nearest
            // venue — an accepted limitation of a best-effort recovery.
            const sorted = [...fallback.data].sort((a, b) => {
              const distA = a.distance_miles ?? Infinity;
              const distB = b.distance_miles ?? Infinity;
              // Avoid Infinity - Infinity = NaN when both are missing.
              return distA === distB ? 0 : distA - distB;
            });
            setFallbackStops(sorted.slice(0, 3));
          } catch {
            // Best-effort recovery only — a failed fallback degrades silently
            // to the plain empty state, never a visible error.
            if (generation === searchGenRef.current) setFallbackStops([]);
          }
        }
      } catch (err) {
        if (generation !== searchGenRef.current) return;
        setError(getFriendlyErrorMessage(err, 'Failed to search events'));
        setEvents([]);
      } finally {
        if (generation === searchGenRef.current) setLoading(false);
      }
    })();
  }, [location.zip, location.lat, location.lng, radius, selectedShow, hasLocation]);

  const handleSearch = useCallback(
    (loc: { zip?: string; lat?: number; lng?: number; displayLabel?: string }) => {
      const newLocation: LocationState = {
        zip: loc.zip,
        lat: loc.lat,
        lng: loc.lng,
        label: loc.displayLabel || loc.zip,
      };
      setLocation(newLocation);

      // Update URL params
      if (loc.lat != null && loc.lng != null) {
        const params: Record<string, string> = {
          lat: loc.lat.toString(),
          lng: loc.lng.toString(),
        };
        if (loc.displayLabel) params.q = loc.displayLabel;
        if (loc.zip) params.zip = loc.zip;
        setSearchParams(params);
      } else if (loc.zip) {
        setSearchParams({ zip: loc.zip });
      }
    },
    [setSearchParams]
  );

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="bg-white border-b border-warm-200 sticky top-0 z-20">
        <div className="container-wide py-4">
          <div className="flex items-center justify-between">
            <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            </Link>
            <div className="flex items-center gap-4">
              {/* Studio nav links — only when authenticated */}
              {isAuthenticated && (
                <nav className="hidden md:flex items-center gap-6">
                  <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">Dashboard</Link>
                  <Link to="/dashboard/bookings" className="text-warm-600 hover:text-warm-900 text-sm">My Bookings</Link>
                  <Link to="/dashboard/inquiries" className="text-warm-600 hover:text-warm-900 text-sm">My Inquiries</Link>
                  <Link to="/dashboard/profile" className="text-warm-600 hover:text-warm-900 text-sm">Studio Profile</Link>
                  <Link to="/dashboard/contacts" className="text-warm-600 hover:text-warm-900 text-sm">Contacts</Link>
                </nav>
              )}

              {/* Cart Indicator — sits between nav links and Sign Out per SDA-149 */}
              <Link
                to="/checkout"
                className="relative flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-warm-100 transition-colors group"
              >
                <svg
                  className="h-6 w-6 text-warm-600 group-hover:text-warm-900 transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                {cartSummary.itemCount > 0 && (
                  <>
                    <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 rounded-full bg-primary-500 text-white text-xs font-bold">
                      {cartSummary.itemCount}
                    </span>
                    <span className="hidden sm:block text-sm font-medium text-warm-700 group-hover:text-warm-900">
                      Cart
                    </span>
                  </>
                )}
                {cartSummary.itemCount === 0 && (
                  <span className="hidden sm:block text-sm text-warm-500">Cart</span>
                )}
              </Link>

              {authLoading ? (
                <span className="text-warm-400">Loading...</span>
              ) : isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="btn-primary"
                >
                  Sign Out
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-warm-600 hover:text-warm-900 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link to="/register" className="btn-primary">
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Search Section */}
      <section className="bg-gradient-to-b from-warm-100 to-warm-50 py-8 border-b border-warm-200">
        <div className="container-wide">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-warm-900 text-center mb-6">
              Find a Broadway Master Class On Tour
            </h1>
            <LocationSearch
              variant="compact"
              onSearch={handleSearch}
              isSearching={loading}
            />
            <p className="mt-3 text-warm-500 text-sm text-center">
              See which Broadway Master Classes On Tour are coming to your area!
            </p>
          </div>
        </div>
      </section>

      {/* Filters & Results */}
      <main className="container-wide py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <label htmlFor="show-filter" className="text-sm font-medium text-warm-700">
              Class:
            </label>
            <select
              id="show-filter"
              value={selectedShow}
              onChange={(e) => setSelectedShow(e.target.value)}
              className="input py-2 min-w-[180px]"
            >
              <option value="">All Classes</option>
              {shows.map((show) => (
                <option key={show.id} value={show.id}>
                  {show.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="radius-filter" className="text-sm font-medium text-warm-700">
              Distance:
            </label>
            <select
              id="radius-filter"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="input py-2"
            >
              <option value={15}>Within 15 miles</option>
              <option value={25}>Within 25 miles</option>
              <option value={50}>Within 50 miles</option>
              <option value={75}>Within 75 miles</option>
              <option value={100}>Within 100 miles</option>
              <option value={150}>Within 150 miles</option>
            </select>
          </div>

          {hasLocation && (
            <div className="ml-auto text-sm text-warm-600">
              Searching near <span className="font-semibold">{location.label || location.zip || 'your location'}</span>
            </div>
          )}
        </div>

        {/* Results */}
        {!hasLocation ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-warm-100 flex items-center justify-center mx-auto mb-6">
              <svg
                className="h-10 w-10 text-warm-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-warm-900 mb-2">
              Search Your Location
            </h2>
            <p className="text-warm-600 max-w-md mx-auto">
              Search by studio name, address, or ZIP code to find Broadway touring
              productions near your dance studio.
            </p>
          </div>
        ) : (
          <>
            {/* Results Header */}
            {!loading && !error && events.length > 0 && (
              <div className="flex items-center justify-between mb-4">
                <p className="text-warm-600">
                  Found <span className="font-semibold text-warm-900">{events.length}</span>{' '}
                  {events.length === 1 ? 'master class option' : 'master class options'} near {location.label || location.zip || 'your location'}
                </p>
              </div>
            )}

            {/* First-search loading: skeleton cards that mirror the result
                layout, so the page reads as "loading content" rather than a
                lone spinner (SDA-104 follow-up). */}
            {loading && events.length === 0 ? (
              <div>
                <p className="text-warm-600 mb-4">
                  Searching for classes near{' '}
                  <span className="font-semibold text-warm-900">
                    {location?.label || location?.zip || 'your location'}
                  </span>
                  …
                </p>
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="bg-white rounded-xl shadow-sm border border-warm-100 p-5 animate-pulse"
                    >
                      <div className="flex items-start gap-4">
                        <div className="h-16 w-16 rounded-lg bg-warm-100 flex-shrink-0" />
                        <div className="flex-1 space-y-2.5">
                          <div className="h-5 bg-warm-100 rounded w-1/2" />
                          <div className="h-4 bg-warm-100 rounded w-1/3" />
                          <div className="h-4 bg-warm-100 rounded w-2/5" />
                        </div>
                        <div className="hidden sm:flex flex-col items-end gap-2">
                          <div className="h-6 bg-warm-100 rounded w-20" />
                          <div className="h-9 bg-warm-100 rounded w-24" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EventList
                events={events}
                isLoading={loading}
                error={error}
                locationQuery={locationQuery}
                fallbackStops={fallbackStops}
                searchRadius={radius}
              />
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-warm-900 text-warm-300 py-8 mt-auto">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-6 brightness-0 invert" />
            </Link>
            <div className="flex gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">
                About
              </Link>
              <Link to="/contact" className="hover:text-white transition-colors">
                Contact
              </Link>
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms
              </Link>
            </div>
            <p className="text-sm">© {new Date().getFullYear()} Stage Door Access</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default EventSearchPage;
